import React from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import type { BankAccountDetails } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { SENSITIVE_IDENTIFIER_KEYS } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { maskString } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';

const FALLBACK_DELETE_CONFIRM_WORD = 'Bank account';

// The value that tells one of a person's accounts from another. Whitespace is stripped
// so a spaced IBAN ("GB29 NWBK ... 19") masks to its real tail rather than "8 19".
const identifyingValue = (bankAccount: BankAccountDetails | null): string => {
	const identifiers = bankAccount?.identifiers ?? [];
	const value =
		bankAccount?.accountNumber ||
		bankAccount?.iban ||
		identifiers.find((identifier) => SENSITIVE_IDENTIFIER_KEYS.has(identifier.key))?.value ||
		identifiers[0]?.value ||
		'';
	return value.replace(/\s+/g, '');
};

interface Props {
	opened: boolean;
	onClose: () => void;
	bankAccount: BankAccountDetails | null;
	handleDelete: () => void;
	deleteLoading: boolean;
	deleteSuccess: boolean;
}

export const BankAccountDeleteDrawer = ({
	opened,
	onClose,
	bankAccount,
	handleDelete,
	deleteLoading,
	deleteSuccess,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const deleteConfirmText =
		bankAccount?.bankName || bankAccount?.accountHolder || FALLBACK_DELETE_CONFIRM_WORD;

	const getDeleteConfirmCopy = () => {
		if (bankAccount?.bankName) {
			return {
				label: tMipManage('settings_modal.bank_account.delete.input_label'),
				placeholder: tMipManage('settings_modal.bank_account.delete.input_placeholder'),
				mismatch: tMipManage('settings_modal.bank_account.delete.word_mismatch_error'),
			};
		}
		if (bankAccount?.accountHolder) {
			return {
				label: tMipManage('settings_modal.bank_account.delete.input_label_holder'),
				placeholder: tMipManage(
					'settings_modal.bank_account.delete.input_placeholder_holder'
				),
				mismatch: tMipManage(
					'settings_modal.bank_account.delete.word_mismatch_error_holder'
				),
			};
		}
		return {
			label: tMipManage('settings_modal.bank_account.delete.input_label_fallback', {
				word: deleteConfirmText,
			}),
			placeholder: deleteConfirmText,
			mismatch: tMipManage('settings_modal.bank_account.delete.word_mismatch_error_fallback'),
		};
	};
	const deleteConfirmCopy = getDeleteConfirmCopy();

	// Two accounts of one holder at no named bank share a confirmation word, so the word
	// alone cannot say which is about to go. The summary identifies the record instead —
	// masked like the tile, so it reads the same as the row behind the drawer.
	const identifier = identifyingValue(bankAccount);
	const accountSummary = [
		bankAccount?.bankName || bankAccount?.accountHolder,
		getCountryNameByCode(bankAccount?.countryCode ?? '') || bankAccount?.countryCode,
		identifier ? maskString(identifier) : '',
		bankAccount?.currency,
	]
		.filter(Boolean)
		.join(' \n ');

	return (
		<ConfirmDeleteDrawer
			opened={opened}
			onClose={onClose}
			title={tMipManage('settings_modal.bank_account.delete.title')}
			alertTitle={
				accountSummary
					? tMipManage('settings_modal.bank_account.delete.alert_title')
					: undefined
			}
			alertDescription={accountSummary || undefined}
			inputLabel={deleteConfirmCopy.label}
			inputPlaceholder={deleteConfirmCopy.placeholder}
			confirmDeleteText={deleteConfirmText}
			wordMismatchErrorMessage={deleteConfirmCopy.mismatch}
			deleteBtnText={tMipManage('settings_modal.bank_account.delete.confirm')}
			cancelBtnText={tMipManage('settings_modal.bank_account.delete.cancel')}
			handleDelete={handleDelete}
			deleteLoading={deleteLoading}
			deleteSuccess={deleteSuccess}
		/>
	);
};
